<template>
  <div class="b-pop-up__content">
    <Alert
      v-if="incompatibleCouponTypes"
      type="danger"
      :messages="[incompatibleCouponTypesError]" />

    <div class="p-booking__modal__title">
      {{ dictionary.voucherTitle }}
    </div>
    <div class="p-booking__modal__desc">
      {{ dictionary.voucherDesc }}
    </div>
    <div class="p-booking__modal__voucher">
      <div>{{ inputTitle }}</div>
      <div class="b-ticket__counter-wrapper">
        <button
          class="btn-icon btn--minus-icon"
          type="button"
          :disabled="store.getUserVouchersCodesModal.length === 1"
          @click="removeUserVoucher"></button>
        <div class="b-ticket__counter">{{ count }}</div>
        <div>
          <button
            id="addVoucherButton"
            type="button"
            class="btn-icon btn-plus-icon"
            :disabled="isDisabledAdd"
            @click="addUserVoucher"></button>
        </div>
      </div>
    </div>
    <Loader v-if="showLoader" />
    <Form v-slot="{ meta, errors }" @submit="submitVoucherData">
      <div class="p-booking__modal__vouchers-wrapper">
        <div
          v-for="(voucher, index) in store.getUserVouchersCodesModal"
          :key="voucher.id"
          class="p-booking__modal__input-wrapper">
          <div class="code-input-group">
            <div class="form-floating">
              <Field
                v-model.trim="voucher.code"
                class="form-control"
                :class="{
                  'form-control--error':
                    (errors[voucher.id] && errors[voucher.id].length) ||
                    !voucher.valid,
                }"
                :name="voucher.id"
                type="text"
                :placeholder="dictionary.voucherCode"
                :maxlength="maxLength"
                :validate-on-input="codeMeta.touched"
                rules="required|voucherCode"
                @blur="codeBlur" />
              <label :for="voucher.id">
                {{ dictionary.voucherCode }}
              </label>
              <ErrorMessage v-slot="{ message }" :name="voucher.id">
                <div class="field-validation-error">
                  {{ showValidationMsg(message, voucher) }}
                </div>
              </ErrorMessage>
              <div v-if="!voucher.valid" class="field-validation-error">
                {{ voucher.message }}
              </div>
            </div>

            <i
              v-if="store.getUserVouchersCodesModal.length > 1"
              class="icon-close"
              :title="dictionary.codeCD4URemove"
              @click="onClickDelete(index)"></i>
          </div>
        </div>
      </div>
      <button
        type="submit"
        class="p-booking__modal__cta btn-primary-orange"
        :disabled="!meta.valid || incompatibleCouponTypes || isDisabledSubmit">
        <span>{{ dictionary.registerCodes }}</span>
      </button>
    </Form>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, nextTick, onMounted, ref } from 'vue'
import { useBookingStore } from '../../../store/BookingStore'
import {
  ErrorMessage,
  Field,
  Form,
  configure,
  defineRule,
  useField,
} from 'vee-validate'
import { required } from '@vee-validate/rules'
import { localize } from '@vee-validate/i18n'
import { getLocale } from '../../../../lib/lib'
import Loader from '../../Shared/Loader.vue'
import Alert from '../../Shared/Alert.vue'
import contants from './../../../../lib/constants'

const { configuration, dictionary } =
  getCurrentInstance().appContext.config.globalProperties
const store = useBookingStore()
const { discount, prepaid } = contants.vouchers
const maxLength = Math.max(discount.length, prepaid.length)

const props = defineProps({
  voucherTitle: { default: '', type: String },
  inputTitle: { default: '', type: String },
  validationError: { default: 'Voucher code is required', type: String },
  voucherCodeValidationError: {
    default: 'Malformed voucher code',
    type: String,
  },
  incompatibleCouponTypesError: {
    default: 'Incompatible coupon types',
    type: String,
  },
  allowDiscountVouchers: { default: true, type: Boolean },
})

defineRule('required', required)
defineRule('voucherCode', (value) =>
  props.allowDiscountVouchers
    ? /^\s*(\d{17}|\d{20})\s*$/.test(value)
    : /^\s*\d{17}\s*$/.test(value)
)

const { handleBlur: codeBlur, meta: codeMeta } = useField(
  'required|voucherCode'
)

const showLoader = ref(false)
const emit = defineEmits(['add-vouchers', 'confirm'])

onMounted(() => {
  configure({
    generateMessage: localize(getLocale(), {
      messages: {
        required: props.validationError,
        voucherCode: props.voucherCodeValidationError,
      },
    }),
  })
  nextTick(() => {
    const addVoucherBtn = document.getElementById('addVoucherButton')
    if (addVoucherBtn && !store.getUserVouchersCodesModal.length) {
      addVoucherBtn.click()
    }
  })
})

const addUserVoucher = () => {
  store.addUserVoucher()
}

const removeUserVoucher = () => {
  store.popVoucherModal()
}

const showValidationMsg = (message, voucher) => {
  return message.replace('{-voucher-}', voucher.code)
}

const submitVoucherData = async () => {
  showLoader.value = true


  try {
    if (
      store.getUserVouchersCodesModal.some((i) => i.code.trim().length === 20 || i.code.trim().length === 17) 
    ) {
      emit('add-vouchers')
        } 
    // else {
    //   emit('confirm')
    // }
  } catch (error) {
    console.error(error)
  }
  showLoader.value = false
}

const count = computed(() => {
  return store.getUserVouchersCodesModal.length
})

const isDisabledAdd = computed(() => {
  console.log(
    'isDisabled',
    store.getSeatsCount,
    store.userVouchersCodesModal.length,
    configuration.maximumTickets
  )
  const sum =
    store.getSeatsCount +
    store.userVouchersCodesModal.length +
    store.getUserVouchersCodes.length

  return sum === configuration.maximumTickets
})

const isDisabledSubmit = computed(() => {
  return store.getSeatsCount === configuration.maximumTickets
})

const incompatibleCouponTypes = computed(() => {
  return !store.getUserVouchersCodesModal
    .map((i) => i.code.trim().length)
    .filter((i) => i)
    .every((l, index, arr) => index === 0 || l === arr[index - 1])
})

const onClickDelete = (index) => {
  store.removeUserVoucherCodeModalByIndex(index)
}
</script>
